import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CANDIDATE_STEPS, EMPLOYER_STEPS } from "../content";
import { PageContainer, SectionHeading, sectionSx } from "./SectionLayout";

function WorkflowRow({ accent, label, steps }) {
  return (
    <Box
      sx={{
        bgcolor: { xs: "background.paper", md: "transparent" },
        border: { xs: "1px solid", md: "none" },
        borderColor: { xs: alpha(accent, 0.16), md: "transparent" },
        borderRadius: { xs: 2.5, md: 0 },
        boxShadow: { xs: "0 14px 36px rgba(55, 36, 111, 0.06)", md: "none" },
        p: { xs: 2.5, sm: 3, md: 0 },
        position: "relative"
      }}
    >
      <Box sx={{ mb: { xs: 3, sm: 4 }, textAlign: "center" }}>
        <Chip
          label={label}
          sx={{
            bgcolor: alpha(accent, 0.1),
            color: accent,
            fontSize: { xs: 12, sm: 12, md: 13 },
            fontWeight: 800,
            letterSpacing: "0.08em",
            minHeight: 30
          }}
        />
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: { xs: 0, sm: 1.5, md: 2 },
          gridTemplateColumns: { xs: "1fr", sm: "repeat(5, minmax(0, 1fr))" },
          position: "relative"
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            borderTop: `2px dashed ${alpha(accent, 0.2)}`,
            display: { xs: "none", sm: "block" },
            left: "10%",
            position: "absolute",
            right: "10%",
            top: 30
          }}
        />
        <Box
          aria-hidden="true"
          sx={{
            borderLeft: `2px dashed ${alpha(accent, 0.2)}`,
            bottom: 26,
            display: { xs: "block", sm: "none" },
            left: 25,
            position: "absolute",
            top: 26
          }}
        />
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isLast = index === steps.length - 1;

          return (
            <Box
              key={step.title}
              sx={{
                alignItems: { xs: "center", sm: "stretch" },
                columnGap: { xs: 2, sm: 0 },
                display: { xs: "grid", sm: "block" },
                gridTemplateColumns: "52px minmax(0, 1fr)",
                minWidth: 0,
                pb: { xs: isLast ? 0 : 2.5, sm: 0 },
                position: "relative",
                textAlign: { xs: "left", sm: "center" },
                width: "100%"
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  bgcolor: isLast ? accent : "background.paper",
                  border: `1px solid ${alpha(accent, 0.12)}`,
                  borderRadius: "50%",
                  boxShadow: "0 10px 30px rgba(93, 63, 211, 0.08)",
                  color: isLast ? "common.white" : accent,
                  display: "flex",
                  height: { xs: 52, sm: 60, md: 62 },
                  justifyContent: "center",
                  mb: { xs: 0, sm: 2 },
                  mx: { xs: 0, sm: "auto" },
                  position: "relative",
                  width: { xs: 52, sm: 60, md: 62 },
                  zIndex: 1
                }}
              >
                <Icon sx={{ fontSize: { xs: 22, sm: 23, md: 24 } }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: { xs: 14, sm: 12, md: 13 }, fontWeight: 800 }}>
                  {step.title}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: { xs: 12, sm: 10, md: 11 }, lineHeight: 1.45, mt: 0.5 }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function WorkflowSection() {
  return (
    <Box
      id="how-it-works"
      component="section"
      sx={{ ...sectionSx, bgcolor: "landing.surfaceLow" }}
    >
      <PageContainer>
        <SectionHeading title="How NextHire Works" />
        <Stack spacing={{ xs: 3, sm: 4, md: 11 }}>
          <WorkflowRow label="For Employers" accent="#451ebb" steps={EMPLOYER_STEPS} />
          <WorkflowRow label="For Candidates" accent="#006c4b" steps={CANDIDATE_STEPS} />
        </Stack>
      </PageContainer>
    </Box>
  );
}
